/**
 * @license
 * @copyright Copyright Motili Inc., 2017 All Rights Reserved
 */

import {
    find,
    findById,
    activateTeamMember as atm,
    deactivateTeamMember as dtm,
    createTeamMember as ctm,
    updateTeamMember as utm,
} from 'common/services/ClientService';
import {
    getChildClientReadPermissionIds,
    getClientId,
} from 'common/services/AuthService';

const teamInclude = {
    relation: 'team',
    scope: {
        fields: ['accountId'],
        include: [
            {
                relation: 'account',
                scope: {
                    fields: [
                        'id',
                        'active',
                        'firstName',
                        'lastName',
                        'timezoneId',
                        'email',
                        'notificationPreferences',
                        'createdAt',
                    ],
                    include: [
                        'childClientPermissions',
                        {
                            relation: 'roles',
                            scope: {
                                fields: ['name', 'description'],
                            },
                        },
                    ],
                },
            },
        ],
    },
};

const companyInclude = [
    {
        relation: 'address',
        scope: {
            fields: [
                'rawAddress',
                'stdAddress',
                'stdDeliveryLine',
                'stdLastLine',
            ],
        },
    },
    {
        relation: 'billingAddress',
        scope: {
            fields: [
                'rawAddress',
                'stdAddress',
                'stdDeliveryLine',
                'stdLastLine',
            ],
        },
    },
    teamInclude,
    'settings',
    'clientUtilities',
];

const companyFields = [
    'id',
    'statusId',
    'name',
    'doingBusinessAs',
    'website',
    'phone',
    'fax',
    'contact',
    'addressId',
    'billingAddressId',
    'operationInCountry',
    'created',
    'updated',
];

const companyWithTeamQuery = {
    filter: {
        include: companyInclude,
        fields: companyFields,
    },
};

// s2 restricts the `team` relation to client admins and owners (client-team-access-utils.js),
// so every read except the Company Management page (e.g. order creation) must
// omit it.
const companyWithoutTeamQuery = {
    filter: {
        include: companyInclude.filter(relation => relation !== teamInclude),
        fields: companyFields,
    },
};

export function getChildClientCompanies(account) {
    const currentClientId = getClientId();
    const clientIds = [currentClientId];
    const readIds = getChildClientReadPermissionIds(account);
    clientIds.push(...readIds);

    const query = {
        filter: {
            ...companyWithoutTeamQuery.filter,
            where: {
                id: { inq: clientIds },
            },
        },
    };

    return find(query);
}

export function getCompany(id) {
    return findById(id, companyWithTeamQuery);
}

export function getClientCompany(id) {
    return findById(id, companyWithoutTeamQuery);
}

export function activateTeamMember(id, teamMemberId) {
    return atm(id, teamMemberId).then(() => getCompany(id));
}

export function deactivateTeamMember(id, teamMemberId) {
    return dtm(id, teamMemberId).then(() => getCompany(id));
}

export function createTeamMember(id, teamMember) {
    return ctm(id, teamMember).then(() => getCompany(id));
}

export function updateTeamMember(id, teamMember) {
    return utm(id, teamMember).then(() => getCompany(id));
}

export function getClientSetting(id) {
    return findById(id, {
        filter: {
            include: ['settings'],
        },
    });
}
